import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sparkles, MessageSquare, Loader2, Globe, Search, CheckCircle2, AlertTriangle, Copy, Check, Clock, Coins, Target } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScoreBadge = ({ score, label }) => {
  const color = score >= 80 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="text-center">
      <div className="relative w-16 h-16 mx-auto mb-1">
        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
          <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#1a1a1a" strokeWidth="3" />
          <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${score}, 100`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold" style={{ color }}>{score}</span>
      </div>
      <span className="text-[10px] text-[#A1A1AA]">{label}</span>
    </div>
  );
};

const AEOPanel = ({ licenseKey }) => {
  const [activeTab, setActiveTab] = useState('analysis');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Analysis state
  const [analysisForm, setAnalysisForm] = useState({ url: '', keyword: '', content: '' });
  const [analysisResult, setAnalysisResult] = useState(null);

  // Content state
  const [contentForm, setContentForm] = useState({
    topic: '', keywords: '', target_engines: ['google_ai', 'chatgpt', 'perplexity'],
    include_schema: true, include_faq: true
  });
  const [contentResult, setContentResult] = useState(null);

  const handleAnalysis = async () => {
    if (!analysisForm.url || !analysisForm.keyword) return;
    const analysisType = activeTab === 'seo' ? 'seo' : activeTab === 'full' ? 'full' : 'aeo';
    setLoading(true);
    setAnalysisResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/aeo-analyze`, {
        url: analysisForm.url,
        keyword: analysisForm.keyword,
        content: analysisForm.content || null,
        license_key: licenseKey,
        analysis_type: analysisType
      });
      setAnalysisResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nell\'analisi AEO');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!contentForm.topic || !contentForm.keywords) return;
    setLoading(true);
    setContentResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/aeo-content`, {
        topic: contentForm.topic,
        keywords: contentForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
        target_engines: contentForm.target_engines,
        include_schema: contentForm.include_schema,
        include_faq: contentForm.include_faq,
        license_key: licenseKey
      });
      setContentResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nella generazione AEO');
    } finally {
      setLoading(false);
    }
  };

  const copyContent = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#8B5CF6] transition-colors";

  const toggleEngine = (engine) => {
    setContentForm(f => ({
      ...f,
      target_engines: f.target_engines.includes(engine)
        ? f.target_engines.filter(e => e !== engine)
        : [...f.target_engines, engine]
    }));
  };

  return (
    <div className="space-y-6" data-testid="aeo-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-['Outfit'] font-medium text-lg">Analisi Competitor</h3>
          <p className="text-xs text-[#A1A1AA]">Analizza qualsiasi sito — ideale per studiare la concorrenza</p>
        </div>
      </div>

      {/* Microcopy Info Cards */}
      <div className="grid grid-cols-3 gap-3" data-testid="aeo-microcopy">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center" data-testid="aeo-info-time">
          <Clock className="w-5 h-5 text-[#8B5CF6] mx-auto mb-2" />
          <span className="block font-['Outfit'] font-semibold text-white text-sm">~30 secondi</span>
          <span className="text-[10px] text-[#A1A1AA]">Tempo di analisi</span>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center" data-testid="aeo-info-cost">
          <Coins className="w-5 h-5 text-[#F59E0B] mx-auto mb-2" />
          <span className="block font-['Outfit'] font-semibold text-white text-sm">3-8 crediti</span>
          <span className="text-[10px] text-[#A1A1AA]">In base al tipo</span>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center" data-testid="aeo-info-score">
          <Target className="w-5 h-5 text-[#10B981] mx-auto mb-2" />
          <span className="block font-['Outfit'] font-semibold text-white text-sm">Score 0-100</span>
          <span className="text-[10px] text-[#A1A1AA]">Punteggio AEO + SEO</span>
        </div>
      </div>

    {/* Tabs */}
    <div className="flex gap-2 flex-wrap">
      <button
        onClick={() => { setActiveTab('seo'); setTimeout(() => { const el = document.querySelector('[data-testid="seo-url-input"]'); if(el) el.scrollIntoView({behavior:'smooth', block:'center'}); }, 100); }}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'seo' ? 'bg-[#0055FF] text-white' : 'bg-white/5 text-[#A1A1AA] hover:text-white'}`}
        data-testid="aeo-tab-seo"
      >
        <span className="inline-flex items-center gap-1.5">🔍 Analisi SEO <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10">3 cred</span></span>
      </button>
      <button
        onClick={() => { setActiveTab('analysis'); setTimeout(() => { const el = document.querySelector('[data-testid="aeo-url-input"]'); if(el) el.scrollIntoView({behavior:'smooth', block:'center'}); }, 100); }}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'analysis' ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-[#A1A1AA] hover:text-white'}`}
        data-testid="aeo-tab-analysis"
      >
        <span className="inline-flex items-center gap-1.5">✨ Analisi AEO <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10">5 cred</span></span>
      </button>
      <button
        onClick={() => { setActiveTab('full'); setTimeout(() => { const el = document.querySelector('[data-testid="full-url-input"]'); if(el) el.scrollIntoView({behavior:'smooth', block:'center'}); }, 100); }}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'full' ? 'bg-gradient-to-r from-[#0055FF] to-[#8B5CF6] text-white' : 'bg-white/5 text-[#A1A1AA] hover:text-white'}`}
        data-testid="aeo-tab-full"
      >
        <span className="inline-flex items-center gap-1.5">🎯 Analisi Completa <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10">8 cred</span></span>
      </button>
    </div>

      {/* Analysis Tab */}
      {activeTab === 'analysis' && (
        <div className="space-y-4">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">URL della pagina</label>
              <input className={inputCls} placeholder="https://tuosito.com/articolo" value={analysisForm.url} onChange={e => setAnalysisForm({ ...analysisForm, url: e.target.value })} data-testid="aeo-url-input" />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Keyword target</label>
              <input className={inputCls} placeholder="es. migliori strategie SEO 2026" value={analysisForm.keyword} onChange={e => setAnalysisForm({ ...analysisForm, keyword: e.target.value })} data-testid="aeo-keyword-input" />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Contenuto (opzionale)</label>
              <textarea className={`${inputCls} min-h-[80px] resize-y`} placeholder="Incolla il contenuto della pagina per un'analisi più' approfondita..." value={analysisForm.content} onChange={e => setAnalysisForm({ ...analysisForm, content: e.target.value })} data-testid="aeo-content-input" />
            </div>
            <Button onClick={handleAnalysis} disabled={loading || !analysisForm.url || !analysisForm.keyword} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-full py-5" data-testid="aeo-analyze-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Analizza AEO
            </Button>
          </div>

          {/* Analysis Results */}
          {analysisResult && (
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-5" data-testid="aeo-analysis-result">
              {/* Scores */}
              <div className="flex justify-center gap-8">
                <ScoreBadge score={analysisResult.aeo_score || 0} label="AEO Score" />
                <ScoreBadge score={analysisResult.seo_score || 0} label="SEO Score" />
                <ScoreBadge score={analysisResult.direct_answer_quality || 0} label="Direct Answer" />
                <ScoreBadge score={analysisResult.citability_score || 0} label="Citabilita'" />
              </div>

              {/* AI Visibility */}
              <div className="text-center">
                <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${analysisResult.ai_visibility === 'high' ? 'bg-[#10B981]/20 text-[#10B981]' : analysisResult.ai_visibility === 'medium' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                  <Globe className="w-4 h-4" />
                  AI Visibility: {analysisResult.ai_visibility?.toUpperCase()}
                </span>
              </div>

              {/* Schema */}
              {analysisResult.schema_markup && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-sm font-medium mb-2">Schema Markup</h4>
                  <div className="flex items-center gap-2 mb-2">
                    {analysisResult.schema_markup.present ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />}
                    <span className="text-xs text-[#A1A1AA]">{analysisResult.schema_markup.present ? 'Schema trovato' : 'Schema mancante'}</span>
                  </div>
                  {analysisResult.schema_markup.missing?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {analysisResult.schema_markup.missing.map((s, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-[10px] bg-[#F59E0B]/20 text-[#F59E0B]">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Issues */}
              {analysisResult.issues?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-[#EF4444]">Problemi trovati</h4>
                  <ul className="space-y-1.5">
                    {analysisResult.issues.map((issue, i) => (
                      <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <span className="text-[#EF4444] mt-0.5">-</span>{issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {analysisResult.improvements?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-[#10B981]">Suggerimenti AEO</h4>
                  <ul className="space-y-1.5">
                    {analysisResult.improvements.map((tip, i) => (
                      <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#10B981] mt-0.5 flex-shrink-0" />{tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sample Questions */}
              {analysisResult.sample_questions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Domande AI potenziali</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysisResult.sample_questions.map((q, i) => (
                      <span key={i} className="px-3 py-1 rounded-full text-[10px] bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-[#A1A1AA]">{q}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SEO Analysis Tab */}
      {activeTab === 'seo' && (
        <div className="space-y-4">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">URL della pagina</label>
              <input className={inputCls} placeholder="https://tuosito.com/articolo" value={analysisForm.url} onChange={e => setAnalysisForm({ ...analysisForm, url: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Keyword target</label>
              <input className={inputCls} placeholder="es. migliori strategie SEO 2026" value={analysisForm.keyword} onChange={e => setAnalysisForm({ ...analysisForm, keyword: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Contenuto (opzionale)</label>
              <textarea className={`${inputCls} min-h-[80px] resize-y`} placeholder="Incolla il contenuto della pagina..." value={analysisForm.content} onChange={e => setAnalysisForm({ ...analysisForm, content: e.target.value })} />
            </div>
            <Button onClick={handleAnalysis} disabled={loading || !analysisForm.url || !analysisForm.keyword} className="w-full bg-[#0055FF] hover:bg-[#0044DD] rounded-full py-5">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Analizza SEO
            </Button>
          </div>
          {analysisResult && (
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-5">
              <div className="flex justify-center gap-8">
                <ScoreBadge score={analysisResult.seo_score || 0} label="SEO Score" />
                <ScoreBadge score={analysisResult.aeo_score || 0} label="AEO Score" />
              </div>
              <div className="text-center">
                <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${analysisResult.ai_visibility === 'high' ? 'bg-[#10B981]/20 text-[#10B981]' : analysisResult.ai_visibility === 'medium' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                  <Globe className="w-4 h-4" />
                  AI Visibility: {analysisResult.ai_visibility?.toUpperCase()}
                </span>
              </div>

              {/* Schema */}
              {analysisResult.schema_markup && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-sm font-medium mb-2">Schema Markup</h4>
                  <div className="flex items-center gap-2 mb-2">
                    {analysisResult.schema_markup.present ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />}
                    <span className="text-xs text-[#A1A1AA]">{analysisResult.schema_markup.present ? 'Schema trovato' : 'Schema mancante'}</span>
                  </div>
                  {analysisResult.schema_markup.missing?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {analysisResult.schema_markup.missing.map((s, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-[10px] bg-[#F59E0B]/20 text-[#F59E0B]">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Issues */}
              {analysisResult.issues?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-[#EF4444]">Problemi trovati</h4>
                  <ul className="space-y-1.5">
                    {analysisResult.issues.map((issue, i) => (
                      <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <span className="text-[#EF4444] mt-0.5">-</span>{issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {analysisResult.improvements?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-[#10B981]">Suggerimenti AEO</h4>
                  <ul className="space-y-1.5">
                    {analysisResult.improvements.map((tip, i) => (
                      <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#10B981] mt-0.5 flex-shrink-0" />{tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sample Questions */}
              {analysisResult.sample_questions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Domande AI potenziali</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysisResult.sample_questions.map((q, i) => (
                      <span key={i} className="px-3 py-1 rounded-full text-[10px] bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-[#A1A1AA]">{q}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Full Analysis Tab */}
      {activeTab === 'full' && (
        <div className="space-y-4">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">URL della pagina</label>
              <input className={inputCls} placeholder="https://tuosito.com/articolo" value={analysisForm.url} onChange={e => setAnalysisForm({ ...analysisForm, url: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Keyword target</label>
              <input className={inputCls} placeholder="es. migliori strategie SEO 2026" value={analysisForm.keyword} onChange={e => setAnalysisForm({ ...analysisForm, keyword: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Contenuto (opzionale)</label>
              <textarea className={`${inputCls} min-h-[80px] resize-y`} placeholder="Incolla il contenuto della pagina..." value={analysisForm.content} onChange={e => setAnalysisForm({ ...analysisForm, content: e.target.value })} />
            </div>
            <Button onClick={handleAnalysis} disabled={loading || !analysisForm.url || !analysisForm.keyword} className="w-full bg-gradient-to-r from-[#0055FF] to-[#8B5CF6] hover:opacity-90 rounded-full py-5">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
              Analisi Completa
            </Button>
          </div>
          {analysisResult && (
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-5">
              <div className="flex justify-center gap-8">
                <ScoreBadge score={analysisResult.aeo_score || 0} label="AEO Score" />
                <ScoreBadge score={analysisResult.seo_score || 0} label="SEO Score" />
                <ScoreBadge score={analysisResult.direct_answer_quality || 0} label="Direct Answer" />
                <ScoreBadge score={analysisResult.citability_score || 0} label="Citabilita'" />
              </div>
              <div className="text-center">
                <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${analysisResult.ai_visibility === 'high' ? 'bg-[#10B981]/20 text-[#10B981]' : analysisResult.ai_visibility === 'medium' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                  <Globe className="w-4 h-4" />
                  AI Visibility: {analysisResult.ai_visibility?.toUpperCase()}
                </span>
              </div>

              {/* Schema */}
              {analysisResult.schema_markup && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-sm font-medium mb-2">Schema Markup</h4>
                  <div className="flex items-center gap-2 mb-2">
                    {analysisResult.schema_markup.present ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />}
                    <span className="text-xs text-[#A1A1AA]">{analysisResult.schema_markup.present ? 'Schema trovato' : 'Schema mancante'}</span>
                  </div>
                  {analysisResult.schema_markup.missing?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {analysisResult.schema_markup.missing.map((s, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-[10px] bg-[#F59E0B]/20 text-[#F59E0B]">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Issues */}
              {analysisResult.issues?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-[#EF4444]">Problemi trovati</h4>
                  <ul className="space-y-1.5">
                    {analysisResult.issues.map((issue, i) => (
                      <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <span className="text-[#EF4444] mt-0.5">-</span>{issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {analysisResult.improvements?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-[#10B981]">Suggerimenti AEO</h4>
                  <ul className="space-y-1.5">
                    {analysisResult.improvements.map((tip, i) => (
                      <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                        <CheckCircle2 className="w-3 h-3 text-[#10B981] mt-0.5 flex-shrink-0" />{tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sample Questions */}
              {analysisResult.sample_questions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Domande AI potenziali</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysisResult.sample_questions.map((q, i) => (
                      <span key={i} className="px-3 py-1 rounded-full text-[10px] bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-[#A1A1AA]">{q}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-4">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Argomento</label>
              <input className={inputCls} placeholder="es. Come ottimizzare il SEO nel 2026" value={contentForm.topic} onChange={e => setContentForm({ ...contentForm, topic: e.target.value })} data-testid="aeo-topic-input" />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-1.5">Keywords (separate da virgola)</label>
              <input className={inputCls} placeholder="seo 2026, ottimizzazione seo, strategie seo" value={contentForm.keywords} onChange={e => setContentForm({ ...contentForm, keywords: e.target.value })} data-testid="aeo-keywords-input" />
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider block mb-2">Target AI Engines</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'google_ai', label: 'Google AI', color: '#4285F4' },
                  { id: 'chatgpt', label: 'ChatGPT', color: '#10B981' },
                  { id: 'perplexity', label: 'Perplexity', color: '#8B5CF6' },
                ].map(engine => (
                  <button key={engine.id} onClick={() => toggleEngine(engine.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${contentForm.target_engines.includes(engine.id) ? 'text-white' : 'border-white/10 text-[#555]'}`}
                    style={contentForm.target_engines.includes(engine.id) ? { backgroundColor: engine.color + '20', borderColor: engine.color + '50', color: engine.color } : {}}
                    data-testid={`aeo-engine-${engine.id}`}
                  >
                    {engine.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
                <input type="checkbox" checked={contentForm.include_schema} onChange={e => setContentForm({ ...contentForm, include_schema: e.target.checked })} className="accent-[#8B5CF6]" />
                Schema.org JSON-LD
              </label>
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
                <input type="checkbox" checked={contentForm.include_faq} onChange={e => setContentForm({ ...contentForm, include_faq: e.target.checked })} className="accent-[#EC4899]" />
                Sezione FAQ
              </label>
            </div>
            <Button onClick={handleGenerate} disabled={loading || !contentForm.topic || !contentForm.keywords} className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] hover:opacity-90 rounded-full py-5" data-testid="aeo-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Genera Contenuto AEO
            </Button>
          </div>

          {/* Content Result */}
          {contentResult && (
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 space-y-4" data-testid="aeo-content-result">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h4 className="font-medium">Contenuto AEO Generato</h4>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6]">{contentResult.word_count} parole</span>
                </div>
                <Button onClick={() => copyContent(contentResult.content)} variant="outline" className="border-white/10 text-xs" data-testid="aeo-copy-btn">
                  {copied ? <><Check className="w-3.5 h-3.5 mr-1" />Copiato</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copia HTML</>}
                </Button>
              </div>

              {/* Features used */}
              <div className="flex flex-wrap gap-1.5">
                {(contentResult.aeo_features || []).filter(Boolean).map((f, i) => (
                  <span key={i} className="px-2 py-0.5 rounded text-[10px] bg-[#10B981]/10 text-[#10B981]">{f}</span>
                ))}
              </div>

              {/* HTML Preview */}
              <div className="bg-white/5 rounded-xl p-5 prose prose-invert prose-sm max-w-none overflow-auto max-h-[500px]"
                dangerouslySetInnerHTML={{ __html: contentResult.content }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AEOPanel;
